import { AfterViewInit, Component, ElementRef, inject, NgZone, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbActiveModal, NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { AccountSelectorComponent } from '../../../../../../_shared/components/account-selector/account-selector.component';
import { FormErrorMessageComponent } from '../../../../../../_shared/components/form-error-message/form-error-message.component';
import { ErrorHandlerService } from '../../../../../../_shared/services/error-handler.service';
import { Account } from '../../../../../../_shared/models';
import { WarehouseType } from '../../../../../settings/parts-and-inventory-settings/enums/parts-and-inventory-settings.enum';
import { WarehouseService } from '../../../../../settings/parts-and-inventory-settings/services/warehouse.service';
import { WarehouseDetailsRequest } from '../../../../../settings/parts-and-inventory-settings/models/warehouse-request.model';

@Component({
  selector: 'app-add-warehouse-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, NgbDropdownModule, AccountSelectorComponent, FormErrorMessageComponent, TranslocoDirective, TranslocoPipe],
  templateUrl: './add-warehouse-modal.component.html',
  styleUrl: './add-warehouse-modal.component.scss'
})
export class AddWarehouseModalComponent implements AfterViewInit, OnDestroy {
  @ViewChild('locationInput') locationInput?: ElementRef<HTMLInputElement>;

  private fb = inject(FormBuilder);
  private modalInstance = inject(NgbActiveModal);
  private toastr = inject(ToastrService);
  private warehouseService = inject(WarehouseService);
  private errorHandlerService = inject(ErrorHandlerService);
  private ngZone = inject(NgZone);
  private transloco = inject(TranslocoService);
  private autocomplete: google.maps.places.Autocomplete | null = null;

  form: FormGroup = this.fb.group({
    name: ['', Validators.required],
    location: ['', Validators.required],
    email: ['', [Validators.required, Validators.email]],
    phoneNumber: ['', Validators.required]
  });

  readonly warehouseTypes: WarehouseType[] = [WarehouseType.External, WarehouseType.InHouse];
  readonly warehouseTypeLabels: Record<WarehouseType, string> = {
    [WarehouseType.External]: this.transloco.translate('parts.modals.addWarehouse.typeExternal'),
    [WarehouseType.InHouse]: this.transloco.translate('parts.modals.addWarehouse.typeInHouse')
  };

  warehouseType: WarehouseType = WarehouseType.External;
  selectedAccount: Account | null = null;
  submitted = false;
  isSaving = false;
  private latitude: number | null = null;
  private longitude: number | null = null;

  get selectedTypeLabel(): string {
    return this.warehouseTypeLabels[this.warehouseType];
  }

  get canSave(): boolean {
    return !!this.selectedAccount && this.form.valid && !this.isSaving;
  }

  ngAfterViewInit(): void {
    void this.initLocationAutocompleteAsync();
  }

  ngOnDestroy(): void {
    if (this.autocomplete) {
      google.maps.event.clearInstanceListeners(this.autocomplete);
      this.autocomplete = null;
    }
  }

  onSelectAccount(account: Account): void {
    this.selectedAccount = account;
  }

  selectType(type: WarehouseType): void {
    this.warehouseType = type;
  }

  onLocationTyped(): void {
    this.latitude = null;
    this.longitude = null;
  }

  async onSubmit(): Promise<void> {
    this.submitted = true;
    if (!this.canSave) {
      return;
    }

    this.isSaving = true;
    const request: WarehouseDetailsRequest = {
      id: null,
      name: (this.form.get('name')?.value ?? '').trim(),
      location: (this.form.get('location')?.value ?? '').trim(),
      warehouseType: this.warehouseType,
      accountId: this.selectedAccount!.id,
      email: (this.form.get('email')?.value ?? '').trim(),
      phoneNumber: (this.form.get('phoneNumber')?.value ?? '').trim(),
      lat: this.latitude ?? 0,
      lng: this.longitude ?? 0
    };

    try {
      const response = await this.warehouseService.saveWarehouse(request);
      this.toastr.success(this.transloco.translate('parts.modals.addWarehouse.toastAdded'));
      this.modalInstance.close(response.data?.warehouseId ?? true);
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.isSaving = false;
        }
      });
    }
  }

  getErrorMessage(field: string): string | null {
    const control = this.form.get(field);
    if (control?.hasError('required')) {
      return this.transloco.translate('parts.modals.addWarehouse.fieldRequired');
    }
    if (field === 'email' && control?.hasError('email')) {
      return this.transloco.translate('parts.modals.addWarehouse.invalidEmail');
    }
    return null;
  }

  closeModal(): void {
    this.modalInstance.dismiss();
  }

  private async initLocationAutocompleteAsync(): Promise<void> {
    if (!this.locationInput?.nativeElement) {
      return;
    }

    try {
      await google.maps.importLibrary('places');
    } catch {
      return;
    }

    this.autocomplete = new google.maps.places.Autocomplete(this.locationInput.nativeElement, {
      types: ['establishment', 'geocode'],
      fields: ['formatted_address', 'name', 'geometry']
    });

    this.autocomplete.addListener('place_changed', () => {
      this.ngZone.run(() => {
        const place = this.autocomplete?.getPlace();
        if (!place) return;

        const location = place.formatted_address || place.name;
        if (location) {
          this.form.get('location')?.setValue(location);
        }

        if (place.geometry?.location) {
          this.latitude = place.geometry.location.lat();
          this.longitude = place.geometry.location.lng();
        }
      });
    });
  }
}
